import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  ElementRef,
} from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Login } from '../models/login';
import { globals } from '../globals';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Configuracao } from '../models/configuracao';
import { AutenticacaoService } from '../services/autenticacao.service';
import { Router, ActivatedRoute } from '@angular/router';
import { AppConfigService } from '../services/app-config.service';
import { CarregandoService } from '../services/carregando.service';
import { MatDialog } from '@angular/material';
import { GlobalService } from '../services/global.service';
import { TipoDeLogin } from '../models/enum';
import { SignalRService } from '../services/signal-r.service';
import { HopperOctopusConfiguration } from "../hopper";
import { CacheService } from "../services/cache-service";
import { TermoAceiteModel } from "../dominio/termo-aceite-model";
import { TermoAceiteModalComponent } from "../modals/termo-aceite-modal/termo-aceite-modal.component";
import { DOCUMENT } from "@angular/common";
import { CaptchaService } from "../services/captcha.service";

@Component({
  selector: 'app-login-gi',
  templateUrl: './login-gi.component.html',
  styleUrls: ['./login-gi.component.scss']
})
export class LoginGIComponent implements OnInit, OnDestroy {
  versao = environment.appVersion;
  anoAtual: Number;
  carregando = false;
  configuracao: Configuracao;
  login = new Login();
  tipoLogin: TipoDeLogin;
  showPassword = false;
  erroAutenticar = false;
  mensagemErro = '';

  urls = [];
  urlSelecionada = globals.apiUrl;

  showCaptcha = false;
  captchaWidgetId: string | null = null;

  private configuracaoSubscription$: Subscription;

  constructor(
    private autenticacaoService: AutenticacaoService,
    private router: Router,
    private appConfigService: AppConfigService,
    private carregandoService: CarregandoService,
    public dialog: MatDialog,
    private globalService: GlobalService,
    private route: ActivatedRoute,
    private signalRService: SignalRService,
    private cacheService: CacheService,
    private captchaService: CaptchaService,
    private elementRef: ElementRef<HTMLElement>,
    @Inject(HopperOctopusConfiguration) private hopperConfigService: HopperOctopusConfiguration,
    @Inject(DOCUMENT) private document: Document,
  ) {
  }

  ngOnInit() {
    this.anoAtual = new Date().getFullYear();
    this.urls = this.appConfigService.getServerUrls();

    if (!this.urls.some(p => p === this.urlSelecionada)) {
      this.urlSelecionada = undefined;
    }

    if (this.urls.length === 1) {
      this.urlSelecionada = this.urls[0];
    }

    this.route.data.subscribe(data => {
      this.tipoLogin = data.tipo;
    });

    // inscreve no evento de troca de configuração
    this.configuracaoSubscription$ = this.globalService.alterouConfiguracao.subscribe(conf => {
      this.configuracao = conf;
    });

    this.globalService.limparUsuarioAtual();
    this.cacheService.clearCachedResponse();
    this.dialog.closeAll();
    this.carregandoService.zera();
    this.autenticacaoService.logoutLocal();

    const body = this.document.getElementsByTagName('body')[0];
    body.classList.add('off-canvas-sidebar');
  }

  ngOnDestroy() {
    this.configuracaoSubscription$.unsubscribe();

    const body = this.document.getElementsByTagName('body')[0];
    body.classList.remove('login-page');
    body.classList.remove('off-canvas-sidebar');
  }

  public onSubmit() {
    this.erroAutenticar = false;
    this.carregando = true;

    globals.apiUrl = this.urlSelecionada;
    localStorage.setItem('urlApi', this.urlSelecionada);

    this.executarCaptcha().finally();
  }

  private async executarCaptcha() {
    // Se não tem captcha configurado, autentica diretamente.
    if (!this.captchaService.captchaHabilitado()) {
      this.autenticar();
      return;
    }

    const containerElement = this.elementRef.nativeElement.querySelector('#challenger-container') as HTMLElement;

    // O container não foi encontrado.
    if (!containerElement) {
      this.erroAoAutenticar("Falha ao executar o script de verificação de segurança. Atualize a página e tente novamente.");
      return;
    }

    try {
      this.showCaptcha = true;

      const captchaScript = await this.captchaService.carregarScript();

      if (!this.captchaWidgetId) {
        this.captchaWidgetId = captchaScript.render(containerElement, {
          sitekey: this.captchaService.buscarChaveCliente(),
          action: "login",
          callback: (token: string) => {
            this.login.desafio = token;

            this.autenticar();
          },
          'error-callback': (errorCode: string) => {
            this.erroAoAutenticar(`Ocorreu um erro ao verificar se você é humano: ${errorCode}`);
          },
          'timeout-callback': () => {
            this.erroAoAutenticar('Tempo limite excedido ao verificar se você é humano. Atualize a página e tente novamente.');
          },
          theme: "light",
          size: "flexible",
          retry: "never",
        });
      } else {
        captchaScript.reset(this.captchaWidgetId);
      }
    }
    catch (erro) {
      this.erroAoAutenticar(erro.message);
    }
  }

  private autenticar() {
    this.carregandoService.mostra('autenticar');

    this.autenticacaoService.login(this.login).subscribe(dados => {
      this.showCaptcha = false;

      if (dados.termosParaAceitar) {
        // Usuário possui termos de aceite pendêntes.
        this.carregandoService.esconde('autenticar');
        this.abrirTermos(dados.termosParaAceitar, dados.usuarioTermo);
      } else {
        this.autenticacaoService.salvarToken(dados);
        this.signalRService.startConnection(dados);

        // Inicializando micro services
        this.hopperConfigService.initializeAsync().then();

        // Redirecionando
        this.router.navigate(['/']).then(() => {
          this.carregandoService.esconde('autenticar');
          this.carregando = false;
        });
      }
    }, (data: any) => {
      let mensagem = 'Erro ao realizar a autenticação do usuário';

      if (data instanceof HttpErrorResponse) {
        if (data.status === 400 && data.error && data.error.errors) {
          mensagem = data.error.errors.map(e => e.reason).join(' ');
        }
      }

      this.erroAoAutenticar(mensagem);
      this.autenticacaoService.logoutLocal();
      this.carregandoService.esconde('autenticar');
    });
  }

  private erroAoAutenticar(erro: string) {
    this.carregando = false;
    this.showCaptcha = false;
    this.erroAutenticar = true;
    this.mensagemErro = erro;

    this.login.senha = null;
    this.login.desafio = null;
  }

  abrirTermos(termos: TermoAceiteModel, usuarioTermo: string) {
    const dialogRef = this.dialog.open(TermoAceiteModalComponent, {
      width: '800px',
      data: {
        termos: termos,
        usuarioTermo: usuarioTermo
      },
      panelClass: ['modal-card', 'modal-card-v2']
    });

    dialogRef.afterClosed().subscribe(async (result: any) => {
      if (result) {
        this.onSubmit();
      }
    });
  }
}

